/**
 * Buyer Negotiation Contract Component
 * Handles contract negotiation from the buyer's perspective
 */

import React, { useMemo, useState } from 'react';
import { ChevronRight, List } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useGetAgreeableClausesQuery } from '@/apis/extended/agreeableClauseApi';
import { useSendMessageMutation } from '@/apis/extended/messageApi';
import {
  useCreateNegotiationMutation,
  useGetCounterProposalQuery,
  useUpdateNegotiationMutation,
} from '@/apis/extended/negotiationApi';
import {
  useCreateBatchProposalsMutation,
  useCreateProposalMutation,
  useGetContractProposalsQuery,
  useRespondToProposalMutation,
  type Proposal,
} from '@/apis/extended/proposalApi';
import { ClauseProposal } from '@/components/Clause/ClauseProposal';
import { NegotiationClause } from '@/components/Clause/NegotiationClause';
import BuyerWaitingState from '@/components/Negotiation/BuyerWaitingState';
import { ComprehensiveClauseDisplay } from '@/components/Negotiation/ComprehensiveClauseDisplay';
import { NegotiationDashboard } from '@/components/Negotiation/NegotiationDashboard';
import { NegotiationStatusBar } from '@/components/Negotiation/NegotiationStatusBar';
import { PartySignersPanel } from '@/components/Negotiation/PartySignersPanel';
import { ProposalClauseDisplay } from '@/components/Negotiation/ProposalClauseDisplay';
import { ProposalSubmissionPanel } from '@/components/Negotiation/ProposalSubmissionPanel';
import { ProposeDictionaryClauseCard } from '@/components/Negotiation/ProposeDictionaryClauseCard';
import { VoidContractButton } from '@/components/Negotiation/VoidContractButton';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useClauseNotifications } from '@/hooks/useClauseNotifications';
import { useClauseState } from '@/hooks/useClauseState';
import { useContractData } from '@/hooks/useContractData';
import { useContractRatification } from '@/hooks/useContractRatification';
import ContractOverallStatus from '../../SellerAgent/Negotiation/Component/ContractOverallStatus';
import NegotiationHeader from '../../SellerAgent/Negotiation/Component/Negotiationheader';

interface CounterProposalState {
  [clauseId: string]: {
    value: any;
    reason: string;
  };
}

type BuyerNegotiateContractPageProps = {
  /**
   * Optional override. When this page is rendered inside the deal-room
   * Stage 6 panel there's no `/buyer/negotiate/:contractId` URL param, so the
   * parent passes the contract id explicitly.
   */
  contractId?: string;
  /** Deal-room Stage 6: drop standalone page chrome and use parent navigation. */
  embedded?: boolean;
  onBack?: () => void;
};

const BuyerNegotiateContractPage = ({
  contractId: contractIdProp,
  embedded = false,
  onBack,
}: BuyerNegotiateContractPageProps = {}) => {
  const { contractId: contractIdParam } = useParams();
  const navigate = useNavigate();

  // Enhanced clause state management
  const contractId = contractIdProp ?? contractIdParam ?? '';
  const pageClass = embedded
    ? 'space-y-6'
    : 'taste-negotiate-page container mx-auto px-4 py-8 space-y-6 taste-rise';
  const handleBack = onBack ?? (() => navigate(-1));
  const afterVoidNavigate = () => navigate('/properties');

  // Use unified contract data hook
  const {
    contract,
    isLoading: contractLoading,
    error: contractError,
    isRatified: contractRatified,
    canNegotiate,
    comprehensiveClauses,
    refetch: refetchContract,
  } = useContractData(contractId, 'buyer');

  const {
    clauses,
    urgentClauses,
    attentionClauses,
    negotiationSummary,
    hasPendingNegotiations,
  } = useClauseState({
    contractId,
    userRole: 'buyer',
    contractRatified,
  });

  const { isRatified, ratificationDate, specialCircumstances, ratifyContract } =
    useContractRatification(contractId, 'buyer');

  const {
    showClauseStatusChangeNotification,
    showClauseActionNotification,
    showContractStatusNotification,
  } = useClauseNotifications(contractId, 'buyer', contractRatified);

  // Counter proposal state
  const [counterProposal, setCounterProposal] = useState<CounterProposalState>(
    {}
  );
  const [contractStatus, setContractStatus] = useState<
    | 'draft'
    | 'under_review'
    | 'suggested'
    | 'counter_proposed'
    | 'contingent'
    | 'rejected'
    | 'take_it_or_leave_it'
    | 'expired'
    | 'agreeable'
    | 'voided'
    | 'finalized'
    | 'reopened'
  >('draft');

  // Batch proposal state
  const [batchProposals, setBatchProposals] = useState<
    Map<
      string,
      {
        clauseId: string;
        clauseHierarchicalId: string;
        clauseTitle: string;
        originalValue: any;
        proposedValue: any;
        message?: string;
      }
    >
  >(new Map());

  // API mutations
  const [updateNegotiation, { isLoading: isUpdating }] =
    useUpdateNegotiationMutation();
  const [createNegotiation, { isLoading: isCreating }] =
    useCreateNegotiationMutation();
  const [createProposal] = useCreateProposalMutation();
  const [respondToProposal] = useRespondToProposalMutation();
  const [sendMessage] = useSendMessageMutation();

  // Get agreeable clauses for progress tracking
  const { data: agreeableClausesData = [] } = useGetAgreeableClausesQuery(
    contractId,
    { skip: !contractId }
  );

  const agreeableClauses = new Set(
    agreeableClausesData.map((c: any) => c.clauseId)
  );

  // Get all proposals for this contract (for ProposalClauseDisplay)
  const { data: allProposalsData } = useGetContractProposalsQuery(
    { contractId },
    { skip: !contractId }
  );

  // Handle both array and object responses from API
  const allProposals = Array.isArray(allProposalsData)
    ? allProposalsData
    : allProposalsData?.proposals || [];

  // Calculate negotiation status
  const acceptedProposals = allProposals.filter(
    (p: any) => p.status === 'accepted'
  );
  const rejectedProposals = allProposals.filter(
    (p: any) => p.status === 'rejected'
  );
  const canSign =
    contract?.contractStatus === 'awaiting_signatures' ||
    contract?.contractStatus === 'awaiting_buyer_signature' ||
    contract?.contractStatus === 'awaiting_seller_signature';
  const negotiationComplete =
    canSign || contract?.contractStatus === 'ratified';

  const proposalTs = (p: Proposal) =>
    new Date(p.updatedAt || p.createdAt || 0).getTime();

  /** One row per clause: only clauses not yet agreeable; prefer buyer-action pending, then any pending, then latest activity. */
  const negotiationDisplayProposals = useMemo(() => {
    if (!contractId || negotiationComplete) return [];

    const agreedIds = new Set(
      agreeableClausesData.map((c: { clauseId: string }) => c.clauseId)
    );
    const nonAgreed = allProposals.filter((p) => !agreedIds.has(p.clauseId));

    const byClause = new Map<string, Proposal[]>();
    for (const p of nonAgreed) {
      const arr = byClause.get(p.clauseId) ?? [];
      arr.push(p);
      byClause.set(p.clauseId, arr);
    }

    const picks: Proposal[] = [];
    for (const list of byClause.values()) {
      const pendingBuyer = list.filter(
        (p) => p.status === 'pending' && p.responseRequiredFrom === 'buyer'
      );
      const pendingAny = list.filter((p) => p.status === 'pending');
      const pool =
        pendingBuyer.length > 0
          ? pendingBuyer
          : pendingAny.length > 0
          ? pendingAny
          : list;
      pool.sort((a, b) => proposalTs(b) - proposalTs(a));
      if (pool[0]) picks.push(pool[0]);
    }
    picks.sort((a, b) => proposalTs(b) - proposalTs(a));
    return picks;
  }, [contractId, negotiationComplete, allProposals, agreeableClausesData]);

  const pendingBuyerResponseCount = useMemo(() => {
    const agreedIds = new Set(
      agreeableClausesData.map((c: { clauseId: string }) => c.clauseId)
    );
    return allProposals.filter(
      (p) =>
        !agreedIds.has(p.clauseId) &&
        p.status === 'pending' &&
        p.responseRequiredFrom === 'buyer'
    ).length;
  }, [allProposals, agreeableClausesData]);

  const flatClauseList = comprehensiveClauses.flatMap((cat) => cat.clauses);
  const agreedIdSet = new Set(
    agreeableClausesData.map((c: { clauseId: string }) => c.clauseId)
  );
  const allClausesMarkedAgreeable =
    flatClauseList.length > 0 &&
    flatClauseList.every((c) => agreedIdSet.has(c.id));

  // Legacy counter-proposal query - disabled in favor of new proposal system
  // const { data: counterProposalData } = useGetCounterProposalQuery(contractId, {
  //   skip: !contractId,
  //   refetchOnMountOrArgChange: true,
  // });

  // Enhanced clause handling methods
  const handleClauseCounter = async (_clauseId: string, _reason?: string) => {};
  const handleClauseAccept = async (_clauseId: string) => {};
  const handleClauseReject = async (_clauseId: string, _reason?: string) => {};
  const handleClauseEdit = async (_clauseId: string, _newValue: any) => {};
  const handleAddendum = async (_clauseId: string) => {};
  const handleExtension = async (_clauseId: string) => {};
  const handleRepairRequest = async (_clauseId: string) => {};

  const handleProposalResponse = async (
    proposalId: string,
    status: 'accepted' | 'rejected',
    message?: string
  ) => {
    try {
      await respondToProposal({
        proposalId,
        data: {
          status,
          responseMessage: message || '',
        },
      }).unwrap();

      toast.success(
        status === 'accepted'
          ? '✓ Proposal accepted! Seller will be notified.'
          : 'Proposal rejected'
      );

      // Refetch contract and proposals to update UI
      refetchContract();
    } catch (error: any) {
      console.error('Error responding to proposal:', error);
      toast.error(error?.data?.error || 'Failed to respond to proposal');
    }
  };

  const handleProposalCreated = async (
    clauseId: string,
    clauseHierarchicalId: string,
    originalValue: any,
    proposedValue: any,
    message?: string,
    linkedClauses?: any[]
  ) => {
    if (!contractId) {
      toast.error('Contract ID is missing - cannot create proposal');
      return;
    }
    try {
      await createProposal({
        contractId,
        clauseId,
        clauseHierarchicalId,
        originalValue,
        proposedValue,
        message,
        linkedClauses: linkedClauses || [],
      }).unwrap();
      const linkedText =
        linkedClauses && linkedClauses.length > 0
          ? ` with ${linkedClauses.length} linked clause${
              linkedClauses.length > 1 ? 's' : ''
            }`
          : '';
      toast.success(`Proposal created successfully${linkedText}`);
    } catch (error) {
      toast.error('Failed to create proposal');
    }
  };

  const handleValueAccepted = (_clauseId: string, _value: any) => {
    toast.success('Clause value accepted');
  };

  const handleValueRejected = (_clauseId: string) => {
    toast.error('Clause value rejected');
  };

  // Batch proposal handlers
  const handleAddToBatch = (
    clauseId: string,
    clauseHierarchicalId: string,
    clauseTitle: string,
    originalValue: any,
    proposedValue: any,
    message?: string
  ) => {
    const newBatch = new Map(batchProposals);
    newBatch.set(clauseId, {
      clauseId,
      clauseHierarchicalId,
      clauseTitle,
      originalValue,
      proposedValue,
      message,
    });
    setBatchProposals(newBatch);
  };

  const handleRemoveFromBatch = (clauseId: string) => {
    const newBatch = new Map(batchProposals);
    newBatch.delete(clauseId);
    setBatchProposals(newBatch);
  };

  const handleClearBatch = () => {
    setBatchProposals(new Map());
  };

  const handleBatchSubmitSuccess = () => {
    setBatchProposals(new Map());
  };

  // Early return for invalid contractId
  if (!contractId) {
    return (
      <div className={pageClass}>
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">
            Invalid Contract ID
          </h2>
          <p className="text-gray-600 mb-4">
            No contract ID provided in the URL.
          </p>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (contractLoading) {
    return (
      <div className={pageClass}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading contract details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (contractError) {
    return (
      <div className={pageClass}>
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">
            Error Loading Contract
          </h2>
          <p className="text-gray-600 mb-4">
            Failed to load contract details. Please try again.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Check if buyer is waiting for seller/agent response
  // Show waiting state if:
  // 1. Contract exists and is in draft/negotiating status
  // 2. No proposals from seller/agent yet (only buyer's own proposals, if any)
  const sellerOrAgentProposals =
    allProposals?.filter(
      (p: any) => p.proposedBy === 'seller' || p.proposedBy === 'agent'
    ) || [];

  const isWaitingForResponse =
    contract &&
    (contract.contractStatus === 'draft' ||
      (contract as any).contractStatus === 'negotiating') &&
    sellerOrAgentProposals.length === 0 &&
    !contractRatified;

  // Handler for sending message to agent
  const handleSendMessageToAgent = async (messageText: string) => {
    await sendMessage({
      contractId,
      toRole: 'agent',
      message: messageText,
    }).unwrap();
  };

  // Buyer Waiting State - Show when offer submitted but no agent/seller response yet
  if (isWaitingForResponse) {
    return (
      <BuyerWaitingState
        contract={contract}
        contractId={contractId}
        onSendMessage={handleSendMessageToAgent}
      />
    );
  }

  // Ratified Contract View - Show special view when contract is fully ratified
  if (
    contractRatified ||
    (contract as any)?.isRatified ||
    contract?.contractStatus === 'ratified'
  ) {
    return (
      <div className={`${pageClass} max-w-4xl`}>
        <Card className="border-green-500 bg-gradient-to-br from-green-50 to-white">
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-green-800 flex items-center gap-3">
              <svg
                className="h-10 w-10"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Contract Fully Ratified!
            </CardTitle>
            <p className="text-green-700 mt-2">
              Congratulations! This purchase agreement has been executed and is
              now legally binding.
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Contract Summary */}
            <div className="bg-white p-6 rounded-lg border shadow-sm space-y-3">
              <h3 className="font-semibold text-lg text-gray-900 mb-4">
                Contract Summary
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Property Address</p>
                  <p className="font-medium">
                    {contract?.sellerPropertyInfo?.propertyAddress ||
                      contract?.agentSellerInfo?.propertyAddress ||
                      contract?.product?.address?.fullAddress ||
                      contract?.product?.address?.street ||
                      'N/A'}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Purchase Price</p>
                  <p className="font-medium text-green-700">
                    $
                    {contract?.offerSpecifics?.offerPrice?.toLocaleString() ||
                      'N/A'}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Closing Date</p>
                  <p className="font-medium">
                    {contract?.timeline?.closingDate
                      ? new Date(
                          contract.timeline.closingDate
                        ).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                      : 'N/A'}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Ratified On</p>
                  <p className="font-medium">
                    {contract?.ratifiedAt
                      ? new Date(contract.ratifiedAt).toLocaleDateString(
                          'en-US',
                          {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          }
                        )
                      : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Signature Status - Only show when contract is ready for signatures */}
            {(() => {
              const canSign =
                contract?.contractStatus === 'awaiting_signatures' ||
                contract?.contractStatus === 'awaiting_buyer_signature' ||
                contract?.contractStatus === 'awaiting_seller_signature';

              if (!canSign) {
                return (
                  <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                    <h3 className="font-semibold text-lg text-gray-900 mb-2">
                      Signatures
                    </h3>
                    <p className="text-sm text-gray-600">
                      Signatures will be available after negotiations are
                      complete and the contract is finalized.
                    </p>
                  </div>
                );
              }

              return (
                <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                  <h3 className="font-semibold text-lg text-blue-900 mb-4">
                    Signature Status
                  </h3>

                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <svg
                        className={`h-6 w-6 ${
                          contract?.buyerSign?.isStored
                            ? 'text-green-600'
                            : 'text-gray-400'
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <div>
                        <p className="font-medium">Buyer Signature</p>
                        <p className="text-sm text-gray-600">
                          {contract?.buyerSign?.isStored
                            ? `Signed by ${
                                contract.buyerSign.name || 'Buyer'
                              } on ${
                                contract.buyerSign.signedAt
                                  ? new Date(
                                      contract.buyerSign.signedAt
                                    ).toLocaleDateString()
                                  : 'N/A'
                              }`
                            : 'Not signed'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <svg
                        className={`h-6 w-6 ${
                          contract?.sellerSign?.isStored
                            ? 'text-green-600'
                            : 'text-gray-400'
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <div>
                        <p className="font-medium">Seller Signature</p>
                        <p className="text-sm text-gray-600">
                          {contract?.sellerSign?.isStored
                            ? `Signed by ${
                                contract.sellerSign.name || 'Seller'
                              } on ${
                                contract.sellerSign.signedAt
                                  ? new Date(
                                      contract.sellerSign.signedAt
                                    ).toLocaleDateString()
                                  : 'N/A'
                              }`
                            : 'Not signed'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Next Steps / Actions */}
            <div className="bg-gray-50 p-6 rounded-lg border">
              <h3 className="font-semibold text-lg text-gray-900 mb-4">
                Next Steps
              </h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">1.</span>
                  <span>
                    Monitor contingency deadlines in your contract dashboard
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">2.</span>
                  <span>Complete financing and appraisal requirements</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">3.</span>
                  <span>Schedule home inspection (if applicable)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">4.</span>
                  <span>
                    Prepare for closing on{' '}
                    {contract?.timeline?.closingDate
                      ? new Date(
                          contract.timeline.closingDate
                        ).toLocaleDateString()
                      : 'scheduled date'}
                  </span>
                </li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={() =>
                  navigate(`/buyer/property-dashboard/${contractId}`)
                }
                className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium min-w-[200px]"
              >
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                  Purchase Dashboard
                </span>
              </button>
              <button
                onClick={() => navigate(`/view-contract/${contractId}`)}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium min-w-[200px]"
              >
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  View Ratified Contract
                </span>
              </button>
              <button
                onClick={() => navigate(`/contract/download/${contractId}`)}
                className="flex-1 px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium min-w-[200px]"
              >
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Download PDF
                </span>
              </button>
              <button
                onClick={() => navigate('/main')}
                className="px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Back to Properties
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={pageClass}>
      <NegotiationHeader
        contractId={contractId}
        onBack={embedded ? undefined : handleBack}
        canNegotiate={canNegotiate}
        headerActions={
          !contractRatified && contract?.contractStatus !== 'voided' ? (
            <VoidContractButton
              contractId={contractId}
              voidedBy="buyer"
              contractStatus={contract?.contractStatus}
              isRatified={contractRatified}
              onVoidSuccess={() => {
                refetchContract();
                afterVoidNavigate();
              }}
              variant="outline"
              size="sm"
              className="shrink-0 border-white/40 bg-white/10 text-white hover:bg-white/20 hover:text-white"
            />
          ) : undefined
        }
      />

      {/* Wave 2.5 — additive dictionary / multi-party (existing negotiation unchanged) */}
      {canNegotiate && contractId && (
        <div className="space-y-4">
          <ProposeDictionaryClauseCard contractId={contractId} />
          {contract && <PartySignersPanel contract={contract} />}
        </div>
      )}

      {!negotiationComplete && (
        <Card
          className={`${
            pendingBuyerResponseCount > 0
              ? 'bg-yellow-50 border-yellow-300'
              : 'bg-blue-50 border-blue-300'
          }`}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              {pendingBuyerResponseCount > 0 ? (
                <>
                  <svg
                    className="h-6 w-6 text-yellow-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <div>
                    <h3 className="font-semibold text-yellow-900">
                      {pendingBuyerResponseCount} Proposal
                      {pendingBuyerResponseCount !== 1 ? 's' : ''} Awaiting Your
                      Response
                    </h3>
                    <p className="text-sm text-yellow-800">
                      Please review and respond to the pending proposals to
                      continue negotiations.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <svg
                    className="h-6 w-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div>
                    <h3 className="font-semibold text-blue-900">
                      Negotiation in Progress
                    </h3>
                    <p className="text-sm text-blue-800">
                      {acceptedProposals.length > 0 &&
                        `${acceptedProposals.length} proposal${
                          acceptedProposals.length !== 1 ? 's' : ''
                        } accepted. `}
                      {rejectedProposals.length > 0 &&
                        `${rejectedProposals.length} proposal${
                          rejectedProposals.length !== 1 ? 's' : ''
                        } rejected. `}
                      Waiting for agent to finalize contract.
                    </p>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {negotiationComplete && (
        <Card className="bg-green-50 border-green-300">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <h3 className="font-semibold text-green-900">
                  Negotiation Complete
                </h3>
                <p className="text-sm text-green-800">
                  All negotiations are complete. The contract is ready for
                  signatures.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Negotiation Dashboard */}
      <div className="mt-6">
        <NegotiationDashboard
          contractId={contractId}
          userRole="buyer"
          showContractStatusCard={false}
          showContractNavActionsCard={false}
          onClauseClick={(clause) => console.log('Clause clicked:', clause)}
          onRatifyContract={ratifyContract}
          onCreateAddendum={handleAddendum}
          onRequestExtension={handleExtension}
          onCreateRepairRequest={handleRepairRequest}
        />
      </div>

      {/* Notifications - Removed as useClauseNotifications doesn't provide these */}

      {/* Unified Negotiation View with Table of Contents */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mt-6">
        {/* Table of Contents - Left Sidebar */}
        <div className="lg:col-span-1">
          <div className="sticky top-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <List className="h-5 w-5" />
                  Table of Contents
                </CardTitle>
              </CardHeader>
              <CardContent className="max-h-[calc(100vh-200px)] overflow-y-auto">
                <div className="space-y-1">
                  {negotiationDisplayProposals.length > 0 ? (
                    negotiationDisplayProposals.map((proposal) => {
                      const clauseInfo = comprehensiveClauses
                        .flatMap((cat) => cat.clauses)
                        .find((c) => c.id === proposal.clauseId);

                      return (
                        <button
                          key={proposal._id}
                          onClick={() => {
                            const element = document.getElementById(
                              `proposal-${proposal._id}`
                            );
                            element?.scrollIntoView({
                              behavior: 'smooth',
                              block: 'center',
                            });
                          }}
                          className="w-full text-left p-2 rounded-md text-sm transition-colors hover:bg-blue-50 hover:text-blue-700 border-l-2 border-blue-500"
                        >
                          <div className="flex items-center justify-between">
                            <span className="truncate font-medium">
                              {clauseInfo?.title || proposal.clauseId}
                            </span>
                            <ChevronRight className="h-4 w-4 flex-shrink-0 text-blue-600" />
                          </div>
                        </button>
                      );
                    })
                  ) : (
                    <div className="text-center py-4 text-gray-500 text-sm">
                      No proposals from seller yet
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Main Content - Right Side */}
        <div className="lg:col-span-3">
          <div className="space-y-6">
            {/* Negotiation Status Bar with Progress */}
            <NegotiationStatusBar
              totalClauses={
                comprehensiveClauses.flatMap((cat) => cat.clauses).length
              }
              agreedClauses={agreeableClauses.size}
              pendingProposals={pendingBuyerResponseCount}
              priceImpact={negotiationDisplayProposals.reduce((total, p) => {
                if (
                  p.clauseId === 'purchase-price' ||
                  p.clauseId === 'offer-price'
                ) {
                  return (
                    total + (Number(p.proposedValue) - Number(p.originalValue))
                  );
                }
                if (
                  p.clauseId === 'seller-assistance-amount' ||
                  p.clauseId === 'seller-assistance'
                ) {
                  return (
                    total + (Number(p.proposedValue) - Number(p.originalValue))
                  );
                }
                return total;
              }, 0)}
              userRole="buyer"
            />

            {/* Header with proposal count */}
            <div className="flex items-center justify-between">
              <h3 className="text-2xl font-bold text-gray-900">
                Seller's Proposals
              </h3>
              {pendingBuyerResponseCount > 0 && (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600">
                    {pendingBuyerResponseCount} proposal
                    {pendingBuyerResponseCount > 1 ? 's' : ''} require your
                    response
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      toast.info(
                        `Review all ${pendingBuyerResponseCount} proposal${
                          pendingBuyerResponseCount > 1 ? 's' : ''
                        } below`
                      );
                    }}
                    className="bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
                  >
                    <List className="h-4 w-4 mr-2" />
                    View All Proposals
                  </Button>
                </div>
              )}
            </div>

            {/* Open clauses only (not yet agreeable); one primary proposal per clause */}
            {negotiationDisplayProposals.length > 0 ? (
              <ProposalClauseDisplay
                proposals={negotiationDisplayProposals}
                allClauses={comprehensiveClauses.flatMap((cat) => cat.clauses)}
                userRole="buyer"
                onProposalCreated={handleProposalCreated}
                onProposalRespond={handleProposalResponse}
                onAddToBatch={handleAddToBatch}
                selectedBatchIds={new Set(batchProposals.keys())}
              />
            ) : (
              <Card>
                <CardContent className="text-center py-8">
                  {negotiationComplete ? (
                    <>
                      <p className="text-gray-500">
                        Negotiation is complete for this contract.
                      </p>
                      <p className="text-sm text-gray-400 mt-2">
                        Use the status section above for next steps.
                      </p>
                    </>
                  ) : allClausesMarkedAgreeable ? (
                    <>
                      <p className="text-gray-500">
                        All clauses are agreed. Nothing left to negotiate here.
                      </p>
                      <p className="text-sm text-gray-400 mt-2">
                        Waiting for your agent to finalize the contract.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-gray-500">
                        No outstanding proposals for open clauses right now.
                      </p>
                      <p className="text-sm text-gray-400 mt-2">
                        If the seller sends a new proposal, it will appear here.
                        You can still use the dashboard above for clause status.
                      </p>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Proposal Submission Panel - Sticky Footer */}
      <ProposalSubmissionPanel
        contractId={contractId}
        selectedProposals={batchProposals}
        onClearAll={handleClearBatch}
        onRemoveProposal={handleRemoveFromBatch}
        onSubmitSuccess={handleBatchSubmitSuccess}
      />
    </div>
  );
};

export default BuyerNegotiateContractPage;
